import { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import DataPreview from "./DataPreview";
import ModelSelection from "./ModelSelection";
import GraphConfig from "./GraphConfig";
import { useGraphConfig } from "@/store/contexts/GraphConfigContext";
import { ShineBorder } from "@/components/magicui/shine-border";
import AiConfig from "./AiConfig";
import AuthContext from "@/store/contexts/AuthContext";
import axios from "@/services/axios";
import { getRelevantConfig, loadData } from "@/lib/load-data";
import { refactor_config } from "@/utils/refactor";
import GraphConfigFormContext, {
  GraphConfigFormProvider,
} from "@/store/contexts/GraphConfigFormContext";
import { GraphConfiguration } from "@/type/common";
import { getDefaultConfig } from "@/helpers/default";
import { useTranslation } from "react-i18next";

const validateCorrectConfig = (config: GraphConfiguration) => {
  if (config.type === "block" && config.block_options.field === "")
    return false;

  return true;
};

const GraphConfigurationModal = ({ open }: { open: any }) => {
  const {
    config,
    setConfig,
    resetConfig,
    step,
    setStep,
    openConfigModal,
    fields,
    showAi,
    setShowAi,
  } = useGraphConfig();

  const { resetLocalConfig } = useContext(GraphConfigFormContext);
  const { t } = useTranslation();

  const { setConfigs } = useContext(AuthContext);
  const [data, setData] = useState([]);
  const [metadata, setMetadata] = useState<any>(null);
  const [enrichedConfig, setEnrichedConfig] =
    useState<GraphConfiguration | null>(null);
  const [error, setError] = useState(false);
  const loadedConfig = useRef<string | null>(null);
  const currentConfigData = useRef<any>(null);

  const handleClose = () => {
    resetConfig();
  };

  const saveConfig = async () => {
    const exist = await axios
      .get(`/odash/data/${config?.id}`)
      .then((e) => e.data)
      .catch(() => null);

    const usedFields = [
      ...(config?.data_source?.groupBy?.map((g: any) => g.field) || []),
      ...(config?.graph_options?.measures?.map((m: any) => m.field) || []),
      ...(config?.table_options?.columns?.map((m: any) => m.field) || []),
    ];

    if (config && exist) {
      config.fields = fields.filter((field: any) =>
        usedFields.includes(field.field)
      );
      const updatedConfig = await axios
        .put(`/odash/data/${config.id}`, config)
        .then((e) => e.data)
        .catch(() => null);
      setConfigs((prev: any) =>
        prev.map((c: any) => (c.id === config.id ? updatedConfig : c))
      );
    } else if (config) {
      config.fields = fields.filter((field: any) =>
        usedFields.includes(field.field)
      );
      const newConfig = await axios
        .post(`/odash/data`, config)
        .then((e) => e.data)
        .catch(() => null);
      setConfigs((prev: any) => [...prev, newConfig]);
    }
    handleClose();
  };

  useEffect(() => {
    if (config && config.id !== currentConfigData.current) {
      setData([]);
      loadedConfig.current = null;
    }
    if (
      config &&
      config.model &&
      (config.type !== "graph" || config.graph_options.measures.length > 0) &&
      (config.type !== "table" || config.table_options.columns.length > 0)
    ) {
      const configToLoad = JSON.stringify(getRelevantConfig(config));
      if (
        configToLoad === loadedConfig.current ||
        !validateCorrectConfig(config)
      ) {
        setEnrichedConfig(config);
        return;
      }

      loadedConfig.current = configToLoad;
      loadData(refactor_config(config, true), true, fields).then((data) => {
        const updatedData = data[0][config.id];
        if (updatedData && updatedData.data) {
          setData(updatedData.data);
          setMetadata(updatedData.metadata || null);

          // Create enriched config with enriched groupBy from backend
          if (updatedData.enriched_group_by && config) {
            const enriched = {
              ...config,
              data_source: {
                ...config.data_source,
                groupBy: updatedData.enriched_group_by,
              },
            };
            setEnrichedConfig(enriched);
          } else {
            setEnrichedConfig(config);
          }

          setError(false);
          currentConfigData.current = config.id;
        } else {
          setError(true);
        }
      });
    }
  }, [config]);

  useEffect(() => {
    open.current = (data: any) => {
      openConfigModal(data);
    };
  }, []);

  if (!config) {
    return null;
  }

  return (
    <Sheet
      open={config !== null}
      onOpenChange={(open) => (open ? {} : handleClose())}
    >
      <SheetContent
        className={`${
          step === "model" && !showAi ? "" : "w-[600px] !max-w-none"
        }`}
      >
        <SheetHeader className={`${step === "model" ? "" : "pb-0"}`}>
          <SheetTitle>
            {step === "model"
              ? t("editor.graphConfig.modal.modelSelectionTitle")
              : t("editor.graphConfig.modal.graphConfigTitle")}
          </SheetTitle>
          {step === "model" && (
            <SheetDescription>
              {t("editor.graphConfig.modal.modelSelectionDescription")}
            </SheetDescription>
          )}
        </SheetHeader>
        {step !== "model" && (
          <DataPreview
            config={
              enrichedConfig
                ? ({
                    ...enrichedConfig,
                    model: config.model || "",
                  } as GraphConfiguration)
                : config
            }
            data={data}
            metadata={metadata}
            error={error}
          />
        )}
        {showAi && <AiConfig />}
        {step === "model" && !showAi && (
          <ModelSelection
            model={config.model}
            onChange={(model: string) => setConfig({ ...config, model })}
          />
        )}
        {step === "config" && !showAi && (
          <GraphConfigFormProvider>
            <GraphConfig />
          </GraphConfigFormProvider>
        )}
        <SheetFooter className="flex flex-row justify-between gap-2 border-t bg-transparent sticky bottom-0 backdrop-blur-xs bg-white/80">
          {!showAi && false && (
            <Button
              className="relative overflow-hidden"
              onClick={() => setShowAi(true)}
            >
              <ShineBorder shineColor={["#A07CFE", "#FE8FB5", "#FFBE7B"]} />{" "}
              {t("editor.graphConfig.modal.buildWithAI")}
            </Button>
          )}
          {!showAi && <div />}
          {showAi && (
            <Button variant="outline" onClick={() => setShowAi(false)}>
              {t("editor.graphConfig.modal.manualConfig")}
            </Button>
          )}
          <div className="flex flex-row gap-2">
            {step === "model" && !showAi ? (
              <Button
                disabled={config.model === ""}
                onClick={() => setStep("config")}
              >
                {t("editor.graphConfig.modal.selectModel")}
              </Button>
            ) : (
              <>
                {!showAi && (
                  <Button
                    variant={"outline"}
                    onClick={() => {
                      setStep("model");
                      resetLocalConfig();
                      setConfig(getDefaultConfig(config.type));
                    }}
                  >
                    {t("editor.graphConfig.modal.changeModel")}
                  </Button>
                )}
                <Button onClick={saveConfig}>
                  {t("editor.graphConfig.modal.saveChanges")}
                </Button>
              </>
            )}
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

export default GraphConfigurationModal;
